import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ParkingSquare } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1673786833558-606e7e1b56f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBSViUyMHJlc29ydCUyMHBhcmslMjBzdW5zZXR8ZW58MHx8fHwxNzc5NjA4MDgyfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@parkdirector.com");
  const [password, setPassword] = useState("admin123");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F4]" data-testid="login-page">
      {/* Left – form */}
      <div className="flex-1 flex flex-col px-8 lg:px-20 py-10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#284B3B] flex items-center justify-center text-white">
            <ParkingSquare size={18} />
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Park Director</div>
            <div className="text-[10px] tracking-widest uppercase text-[#737A76]">Resort Manager</div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full">
          <div className="mb-8">
            <h1 className="text-4xl tracking-tight font-semibold text-[#1A211D]" style={{ fontFamily: "Outfit" }}>
              Welcome back.
            </h1>
            <p className="text-sm text-[#737A76] mt-2">
              Sign in to manage your 55+ resort park — tenants, lots, reservations, and billing.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#737A76] mb-1.5 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11 bg-white border-[#EAEAE5]"
                placeholder="you@parkdirector.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#737A76] mb-1.5 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-11 bg-white border-[#EAEAE5]"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#284B3B] hover:bg-[#1E3F32] text-white font-medium tracking-tight"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 p-4 rounded-lg bg-[#F0F0EC] border border-[#EAEAE5] text-xs text-[#737A76]">
            <div className="font-medium uppercase tracking-wider text-[10px] mb-1.5 text-[#1A211D]">Demo Credentials</div>
            <div>Admin: <code>admin@parkdirector.com</code> / <code>admin123</code></div>
            <div>Staff: <code>staff@parkdirector.com</code> / <code>staff123</code></div>
          </div>
        </div>

        <div className="text-[11px] text-[#737A76] tracking-wide">© {new Date().getFullYear()} Park Director — Modern RV resort management.</div>
      </div>

      {/* Right – hero */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src={HERO_IMG} alt="Sunset RV resort" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1A211D]/55 via-[#1A211D]/25 to-transparent" />
        <div className="relative z-10 self-end p-12 text-white max-w-lg">
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3 opacity-80">A modern replacement for FileMaker</div>
          <h2 className="text-3xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
            Everything to run your 55+ RV resort, beautifully in one place.
          </h2>
        </div>
      </div>
    </div>
  );
}
